import { BarSeriesOption, DatasetComponentOption, EChartsType, SeriesOption } from 'echarts';
import { RefObject } from 'react';
import {
  RealIntervalFields,
  addEmptyStack,
  getIntervalDataSet,
} from '@/lowcode/common/setter/interval-setter/util';
import {
  calcGridBottom,
  getDataZoomConfig,
  getMobileDataZoomConfig,
  getFormattedValue,
  getIntervalLabel,
  getStackIntervalLabel,
  getYFieldConfig,
  getXFieldConfig,
  getLegendConfig,
} from '../util';
import { themeMap, defaultTheme } from '../../types';
import { CnColumnStyle, ColumnChartCategory } from './type';

export function getColumnCharOptions(chartStyle: CnColumnStyle) {
  const { xFieldConfig, yFieldConfig, legendConfig, themeColor } = chartStyle;
  const { yFieldTitle: originalYFieldTitle } = yFieldConfig || {};
  const { xFieldTitle, showScrollbar = false } = xFieldConfig || {};
  const { showLegend } = legendConfig || {};

  const yFieldTitle = getFormattedValue(originalYFieldTitle) as string;

  const offsetLeft = yFieldTitle ? 24 : 0;

  return {
    color: themeMap[themeColor || defaultTheme].color,
    ...getDataZoomConfig(showScrollbar),
    legend: getLegendConfig(showLegend, { left: offsetLeft }),
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: getXFieldConfig(xFieldConfig),
    yAxis: getYFieldConfig(yFieldConfig),
    grid: {
      top: showLegend ? 32 : 12,
      left: offsetLeft,
      right: 12,
      bottom: calcGridBottom(xFieldTitle, showScrollbar),
      containLabel: true,
    },
    style: {
      maxWidth: 50,
    },
    media: [
      {
        query: { maxWidth: 500 },
        option: {
          ...getMobileDataZoomConfig(showScrollbar),
          tooltip: { triggerOn: 'click' },
        },
      },
    ],
  };
}

export function getColumnChartSeries(
  listData: any[],
  intervalFields: RealIntervalFields,
  chartStyle: CnColumnStyle,
): {
  dataset: DatasetComponentOption;
  series: SeriesOption[];
} {
  const { category, labelConfig, toolTipConfig } = chartStyle;
  const isStackedColumn = category === ColumnChartCategory.STACKED_COLUMN;
  const baseDataSet = getIntervalDataSet(listData, intervalFields);
  const { formatToolTipValue } = toolTipConfig || {};

  const { dataset, series } = isStackedColumn ? addEmptyStack(baseDataSet) : baseDataSet;

  return {
    dataset,
    series: series?.map((s, order) => {
      const isLastSeries = order === series.length - 1;
      const options = isStackedColumn
        ? getStackIntervalLabel(labelConfig, isLastSeries, baseDataSet.yFieldKeys)
        : getIntervalLabel(labelConfig);

      return {
        type: 'bar',
        stack: isStackedColumn ? 'default' : undefined,
        barMaxWidth: 32,
        tooltip: {
          valueFormatter: (value: number | string) => {
            return getFormattedValue(formatToolTipValue || value, value);
          },
        },
        ...s,
        ...options,
      };
    }) as BarSeriesOption[],
  };
}

export const getColumnLegendChangeCallback =
  () =>
  ({
    selectedItems,
    datasets,
    echartRef,
  }: {
    selectedItems: Record<string, boolean>;
    datasets: Array<{
      source?: Record<string, unknown>;
      dimensions?: Array<Record<string, string>>;
    }>;
    echartRef: RefObject<EChartsType>;
  }) => {
    // 过滤未选择中的dataset数据
    const datasetResets = datasets.map((item) => {
      if (typeof item.dimensions !== 'object') {
        return item;
      }

      // 需要重置的维度 key 列表
      const dimensionKeyNames = item?.dimensions?.reduce<string[]>((prev, dimension) => {
        const dimensionDisplayName =
          typeof dimension === 'string' ? dimension : dimension?.displayName || dimension?.name;
        const dimensionKeyName =
          typeof dimension === 'string' ? dimension : dimension?.name || dimension?.displayName;

        const isResetKey =
          dimensionDisplayName &&
          dimensionKeyName &&
          selectedItems?.[dimensionDisplayName] === false;

        return isResetKey ? prev.concat(dimensionKeyName) : prev;
      }, [] as string[]);

      if (!Array.isArray(item.source)) {
        return item;
      }

      return {
        ...item,
        source: item.source?.map((sourceItem) => {
          return Object.entries(sourceItem).reduce((prev, [key, value]) => {
            if (dimensionKeyNames.includes(key)) {
              return {
                ...prev,
              };
            }
            return {
              ...prev,
              [key]: value,
            };
          }, {});
        }),
      };
    });

    echartRef?.current?.setOption({
      dataset: datasetResets,
    });
  };
